import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CourseCard } from '../components/Cards';
import { MapPin, BookOpen, Users, Monitor, Calendar, X } from 'lucide-react';

const defaultFilters = { city: '', course_type: 'all', is_online: 'all', target_audience: 'all', date_from: '', date_to: '' };

export default function CoursesPage() {
  const { t, i18n } = useTranslation();
  const { courses, fetchCourses } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);

  // Check if any filter is active
  const hasActiveFilters = filters.city !== '' || 
    filters.course_type !== 'all' || 
    filters.is_online !== 'all' || 
    filters.target_audience !== 'all' || 
    filters.date_from !== '' || 
    filters.date_to !== '';

  const clearFilters = () => setFilters(defaultFilters);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const params = { ...filters };
      if (params.course_type === 'all') delete params.course_type;
      if (params.is_online === 'true') params.is_online = true;
      else if (params.is_online === 'false') params.is_online = false;
      else delete params.is_online;
      if (params.target_audience === 'all') delete params.target_audience;
      if (!params.date_from) delete params.date_from;
      if (!params.date_to) delete params.date_to;
      await fetchCourses(params);
      setLoading(false);
    };
    load();
  }, [fetchCourses, filters]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 animate-fade-in" data-testid="courses-page">
      <h1 className="font-serif text-4xl font-bold text-primary mb-8">{t('course.title')}</h1>
      
      {/* Filters with labels */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
        {/* Location filter */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-1.5">
            <MapPin className="h-4 w-4" />
            {t('common.location')}
          </label>
          <Input 
            placeholder={t('filters.locationPlaceholder')}
            value={filters.city} 
            onChange={(e) => setFilters({ ...filters, city: e.target.value })} 
            className="w-full"
            data-testid="filter-location"
          />
        </div>
        
        {/* Course type filter */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-1.5">
            <BookOpen className="h-4 w-4" />
            {t('course.type')}
          </label>
          <Select value={filters.course_type} onValueChange={(v) => setFilters({ ...filters, course_type: v })}>
            <SelectTrigger className="w-full" data-testid="filter-type">
              <SelectValue placeholder={t('filters.allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="retreat">{t('course.types.retreat')}</SelectItem>
              <SelectItem value="workshop">{t('course.types.workshop')}</SelectItem>
              <SelectItem value="training">{t('course.types.training')}</SelectItem>
              <SelectItem value="festival">{t('course.types.festival')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Target audience filter */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-1.5">
            <Users className="h-4 w-4" />
            {t('filters.reservedFor')}
          </label>
          <Select value={filters.target_audience} onValueChange={(v) => setFilters({ ...filters, target_audience: v })}>
            <SelectTrigger className="w-full" data-testid="filter-audience">
              <SelectValue placeholder={t('filters.allAudiences')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="women">{t('audience.women')}</SelectItem>
              <SelectItem value="men">{t('audience.men')}</SelectItem>
              <SelectItem value="couples">{t('audience.couples')}</SelectItem>
              <SelectItem value="singles">{t('audience.singles')}</SelectItem>
              <SelectItem value="lgbtqia">{t('audience.lgbtqia')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Modality filter */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-1.5">
            <Monitor className="h-4 w-4" />
            {t('filters.modality')}
          </label>
          <Select value={filters.is_online} onValueChange={(v) => setFilters({ ...filters, is_online: v })}>
            <SelectTrigger className="w-full" data-testid="filter-modality">
              <SelectValue placeholder={t('filters.allModalities')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="true">{t('common.online')}</SelectItem>
              <SelectItem value="false">{t('filters.inPerson')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Date From filter */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-1.5">
            <Calendar className="h-4 w-4" />
            {t('filters.dateFrom')}
          </label>
          <Input 
            type="date"
            value={filters.date_from} 
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} 
            className="w-full"
            data-testid="filter-date-from"
          />
        </div>
        
        {/* Date To filter */}
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-secondary mb-1.5">
            <Calendar className="h-4 w-4" />
            {t('filters.dateTo')}
          </label>
          <Input 
            type="date"
            value={filters.date_to} 
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} 
            className="w-full"
            data-testid="filter-date-to"
          />
        </div>
        
        {/* Clear filters button */}
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="w-full h-10 text-sm"
            data-testid="clear-filters-btn"
          >
            <X className="h-4 w-4 mr-1.5" />
            {t('filters.clear')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><p className="text-secondary">{t('common.loading')}</p></div>
      ) : courses.length === 0 ? (
        <div className="text-center py-12"><p className="text-secondary">{t('common.noResults')}</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} language={i18n.language} />
          ))}
        </div>
      )}
    </div>
  );
}
